import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import {
  Users,
  MessageCircle,
  Mail,
  Phone,
  Video,
  Send,
  ChevronRight,
  ArrowLeft,
  Plus,
  Search,
  User,
  Building,
  Tag,
  CheckCircle2,
  FolderKanban,
  Clock,
  Calendar,
  FileText,
  Edit,
  Inbox,
  Link2,
} from 'lucide-react';
import { HubChannelGroups, HubEmailChannel, LinksChannelView, type ChannelType } from '../components/hub';
import { useAppShell } from '../layouts/AppShell';
import { useTranslation } from '../hooks';
import { useHubStore, useProjectsStore } from '@business-assistant/shared';
import { getNow } from '@business-assistant/shared-data';
import type { Conversation, MessageItem, Contact, Channel, Task, Project } from '@business-assistant/shared/domain';
import { renderWidgetByType } from '../components/common/registry/ComponentRouter';
import type { ContactFormData } from '../components/hub';
import { uniqueById } from '../utils/arrayUtils';
import { listFolders, type ImapFolder } from '@business-assistant/shared';
import { useHubDeepLink, useEntityModal, type HubChannel } from '../hooks';

// =============================================================================
// RE-EXPORTS (backward compatibility)
// =============================================================================

export type ContactKind = 'favorite' | 'recent' | 'lead' | 'team';

export interface ContactItem {
  id: string;
  name: string;
  company?: string;
  role: string;
  kind: ContactKind;
  lastContact: string;
  nextAction?: string;
  tags: Array<{ label: string; variant?: 'warn' | 'ok' | 'blue' }>;
  time: string;
  icon?: 'user' | 'chat';
  prompt?: string;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const CHANNEL_CONFIG: Record<Channel, { icon: typeof Mail; labelKey: string; color: string }> = {
  email: { icon: Mail, labelKey: 'hub.channels.email', color: '#3b82f6' },
  whatsapp: { icon: MessageCircle, labelKey: 'hub.channels.whatsapp', color: '#22c55e' },
  telegram: { icon: Send, labelKey: 'hub.channels.telegram', color: '#0088cc' },
  call: { icon: Phone, labelKey: 'hub.channels.call', color: '#f59e0b' },
  videocall: { icon: Video, labelKey: 'hub.channels.videocall', color: '#8b5cf6' },
  sms: { icon: MessageCircle, labelKey: 'hub.channels.sms', color: '#6b7280' },
};

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

function ChannelIcon({ channel, size = 16 }: { channel: Channel; size?: number }) {
  const config = CHANNEL_CONFIG[channel];
  const Icon = config.icon;
  return <Icon size={size} style={{ color: config.color }} />;
}

function formatTime(isoDate: string, t: (key: string, params?: Record<string, unknown>) => string, locale: string): string {
  const date = new Date(isoDate);
  const now = new Date(getNow()); // Use DEMO_TODAY in demo mode
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return t('hub.now');
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  if (diffDays === 1) return t('common.yesterday');
  if (diffDays < 7) return t('common.daysAgo', { count: diffDays });
  return date.toLocaleDateString(locale, { day: '2-digit', month: 'short' });
}

// =============================================================================
// INBOX LIST VIEW
// =============================================================================

function InboxListView({ onSelectConversation }: { onSelectConversation: (id: string) => void }) {
  const { t, language } = useTranslation();
  const locale = language === 'it' ? 'it-IT' : 'en-US';
  const { openPrompt, showToast } = useAppShell();
  const { conversations, getFilteredConversations, activeFilterKey, getUnreadConversations } = useHubStore();

  const filteredConversations = uniqueById(getFilteredConversations());
  const unreadCount = getUnreadConversations().length;

  // Sort by lastAt descending
  const sortedConversations = [...filteredConversations].sort(
    (a, b) => new Date(b.lastAt).getTime() - new Date(a.lastAt).getTime()
  );

  return (
    <>
      {/* Stats */}
      <div className="section section--padded" style={{ paddingBottom: 0 }}>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <StatBadge label={t('hub.total')} count={conversations.length} variant="blue" />
          {unreadCount > 0 && (
            <StatBadge label={t('hub.unread')} count={unreadCount} variant="warn" />
          )}
        </div>
      </div>

      {/* Conversation list */}
      <div className="list">
        {sortedConversations.map((conv) => (
          <div
            key={conv.id}
            className="item"
            onClick={() => onSelectConversation(conv.id)}
            style={{ background: (conv.unread || 0) > 0 ? 'var(--brand-soft, #ecfdf5)' : undefined }}
          >
            <div className="item__icon">
              <ChannelIcon channel={conv.channel} size={18} />
            </div>
            <div className="item__main">
              <h3 className="item__title" style={{ fontWeight: (conv.unread || 0) > 0 ? 600 : 500 }}>
                {conv.contactName || conv.title}
              </h3>
              <div className="item__meta">
                <span className="tag">{t(CHANNEL_CONFIG[conv.channel].labelKey)}</span>
                {conv.projectId && <span className="tag tag--blue">{t('hub.project')}</span>}
              </div>
              <p style={{
                margin: '0.25rem 0 0',
                fontSize: '0.8125rem',
                color: 'var(--text-secondary)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {conv.title}
              </p>
            </div>
            <div className="item__right">
              <span className="item__time">{formatTime(conv.lastAt, t, locale)}</span>
              {(conv.unread || 0) > 0 && (
                <span style={{
                  minWidth: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: 'var(--brand)',
                  color: 'white',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {conv.unread}
                </span>
              )}
              <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />
            </div>
          </div>
        ))}

        {sortedConversations.length === 0 && (
          <div className="section section--padded" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
            <MessageCircle size={32} style={{ marginBottom: '0.5rem', opacity: 0.5 }} />
            <p>{t('hub.noConversationsFor', { filter: activeFilterKey })}</p>
          </div>
        )}
      </div>
    </>
  );
}

// =============================================================================
// THREAD VIEW
// =============================================================================

function ThreadView({ conversationId, onBack }: { conversationId: string; onBack: () => void }) {
  const { t, language } = useTranslation();
  const locale = language === 'it' ? 'it-IT' : 'en-US';
  const { openPrompt } = useAppShell();
  const { getConversation, getConversationMessages, getConversationContact, markAsRead, addMessage } = useHubStore();
  const [inputText, setInputText] = useState('');

  const conversation = getConversation(conversationId);
  const messages = uniqueById(getConversationMessages(conversationId));
  const contact = getConversationContact(conversationId);

  // Mark as read when opening
  useEffect(() => {
    if (conversation?.unread) {
      markAsRead(conversationId);
    }
  }, [conversationId, conversation?.unread, markAsRead]);

  if (!conversation) return null;

  const handleSend = () => {
    if (!inputText.trim()) return;
    addMessage(conversationId, {
      text: inputText.trim(),
      direction: 'out',
    });
    setInputText('');
  };

  const handleAIReply = () => {
    openPrompt(t('hub.replyTo', { name: conversation.contactName || 'this message' }));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.75rem',
        padding: '0.75rem 1rem', borderBottom: '1px solid var(--line)',
      }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', padding: '0.25rem', cursor: 'pointer' }}>
          <ArrowLeft size={20} />
        </button>
        <ChannelIcon channel={conversation.channel} size={20} />
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>
            {conversation.contactName || conversation.title}
          </h2>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            {t(CHANNEL_CONFIG[conversation.channel].labelKey)}
            {contact?.company && ` • ${contact.company}`}
          </span>
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflow: 'auto', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {messages.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '2rem' }}>
            <MessageCircle size={32} style={{ opacity: 0.5, marginBottom: '0.5rem' }} />
            <p>{t('hub.noMessages')}</p>
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.id}
              style={{
                maxWidth: '80%',
                padding: '0.75rem 1rem',
                borderRadius: '1rem',
                alignSelf: msg.direction === 'out' ? 'flex-end' : 'flex-start',
                background: msg.direction === 'out' ? 'var(--brand)' : 'var(--bg)',
                color: msg.direction === 'out' ? 'white' : 'var(--text)',
                borderBottomRightRadius: msg.direction === 'out' ? '4px' : '1rem',
                borderBottomLeftRadius: msg.direction === 'in' ? '4px' : '1rem',
              }}
            >
              <p style={{ margin: 0, fontSize: '0.875rem', whiteSpace: 'pre-wrap' }}>{msg.text}</p>
              <span style={{
                display: 'block',
                marginTop: '0.25rem',
                fontSize: '0.625rem',
                opacity: 0.7,
                textAlign: 'right',
              }}>
                {new Date(msg.at).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          ))
        )}
      </div>

      {/* Input */}
      <div style={{
        padding: '0.75rem 1rem',
        borderTop: '1px solid var(--line)',
        display: 'flex', gap: '0.5rem', alignItems: 'flex-end',
      }}>
        <textarea
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          placeholder={t('hub.writeMessage')}
          style={{
            flex: 1,
            minHeight: '40px',
            maxHeight: '120px',
            padding: '0.5rem 0.75rem',
            border: '1px solid var(--line)',
            borderRadius: '1rem',
            resize: 'none',
            fontSize: '0.875rem',
            fontFamily: 'inherit',
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <button
          onClick={handleAIReply}
          style={{
            padding: '0.5rem 0.75rem',
            border: 'none',
            borderRadius: '0.5rem',
            background: 'var(--brand-soft)',
            color: 'var(--brand)',
            cursor: 'pointer',
            fontSize: '0.75rem',
            fontWeight: 500,
          }}
        >
          AI
        </button>
        <button
          onClick={handleSend}
          disabled={!inputText.trim()}
          style={{
            width: '40px',
            height: '40px',
            border: 'none',
            borderRadius: '50%',
            background: inputText.trim() ? 'var(--brand)' : 'var(--line)',
            color: inputText.trim() ? 'white' : 'var(--text-muted)',
            cursor: inputText.trim() ? 'pointer' : 'default',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Send size={18} />
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// CONTACTS LIST VIEW
// =============================================================================

function ContactsListView({
  onSelectContact,
  selectedContactId,
  onCloseContact,
}: {
  onSelectContact: (id: string) => void;
  selectedContactId?: string;
  onCloseContact?: () => void;
}) {
  const { t } = useTranslation();
  const { showToast } = useAppShell();
  const { contacts, createContact, updateContact, getContact } = useHubStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [showContactForm, setShowContactForm] = useState(false);
  const [editingContactId, setEditingContactId] = useState<string | undefined>();

  // Get selected contact for widget
  const selectedContact = selectedContactId ? getContact(selectedContactId) : null;

  const filteredContacts = uniqueById(
    searchQuery.trim()
      ? contacts.filter((c) =>
          c.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          c.company?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          c.handles.email?.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : contacts
  );

  const handleAddContact = () => {
    setEditingContactId(undefined);
    setShowContactForm(true);
  };

  const handleEditContact = (contactId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation to detail view
    setEditingContactId(contactId);
    setShowContactForm(true);
  };

  const handleSaveContact = (data: ContactFormData) => {
    if (editingContactId) {
      updateContact(editingContactId, data);
      showToast(t('hub.contactUpdated'));
    } else {
      createContact(data);
      showToast(t('hub.contactCreated'));
    }
    setShowContactForm(false);
    setEditingContactId(undefined);
  };

  return (
    <>
      {/* Search */}
      <div className="section section--padded" style={{ paddingBottom: 0 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          padding: '0.5rem 0.75rem',
          background: 'var(--bg)', borderRadius: '0.5rem',
        }}>
          <Search size={16} style={{ color: 'var(--text-muted)' }} />
          <input
            type="text"
            placeholder={t('hub.searchContacts')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              flex: 1, border: 'none', background: 'transparent',
              fontSize: '0.875rem', outline: 'none',
            }}
          />
        </div>
      </div>

      {/* Contact list */}
      <div className="list">
        {filteredContacts.map((contact) => (
          <div
            key={contact.id}
            className="item"
            onClick={() => onSelectContact(contact.id)}
            onDoubleClick={(e) => handleEditContact(contact.id, e)}
            style={{ cursor: 'pointer' }}
          >
            <div className="item__icon">
              <div style={{
                width: '36px', height: '36px', borderRadius: '50%',
                background: 'var(--brand)', color: 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.875rem', fontWeight: 600,
              }}>
                {contact.displayName.charAt(0).toUpperCase()}
              </div>
            </div>
            <div className="item__main">
              <h3 className="item__title">{contact.displayName}</h3>
              <div className="item__meta">
                {contact.company && <span>{contact.company}</span>}
                {contact.role && <span className="tag">{contact.role}</span>}
              </div>
              {contact.handles.email && (
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  {contact.handles.email}
                </p>
              )}
            </div>
            <div className="item__right">
              <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />
            </div>
          </div>
        ))}

        {filteredContacts.length === 0 && (
          <div className="section section--padded" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
            <Users size={32} style={{ marginBottom: '0.5rem', opacity: 0.5 }} />
            <p>{searchQuery ? t('common.noResults') : t('hub.noContacts')}</p>
            <button className="btn btn--primary" style={{ marginTop: '1rem' }} onClick={handleAddContact}>
              <Plus size={16} /> {t('hub.addContact')}
            </button>
          </div>
        )}
      </div>

      {filteredContacts.length > 0 && (
        <div className="section section--padded">
          <button className="btn btn--outline" onClick={handleAddContact} style={{ width: '100%' }}>
            <Plus size={16} /> {t('hub.newContact')}
          </button>
        </div>
      )}

      {/* Contact Form Modal - Uses centralized ComponentRouter */}
      {showContactForm && renderWidgetByType('cnt', {
        entityId: editingContactId,
        onClose: () => {
          setShowContactForm(false);
          setEditingContactId(undefined);
        },
        onSave: handleSaveContact,
      })}

      {/* Contact Detail Panel - opened via URL */}
      {selectedContact && renderWidgetByType('cnt', {
        entityId: selectedContactId,
        onClose: onCloseContact,
        onSave: (data: ContactFormData) => {
          if (selectedContactId) {
            updateContact(selectedContactId, data);
            showToast(t('hub.contactUpdated'));
          }
        },
      })}
    </>
  );
}

// =============================================================================
// CONTACT DETAIL VIEW (Enhanced with Tasks & Projects)
// =============================================================================

function ContactDetailView({ contactId, onBack, onEdit }: { contactId: string; onBack: () => void; onEdit?: () => void }) {
  const { t, language } = useTranslation();
  const locale = language === 'it' ? 'it-IT' : 'en-US';
  const { openPrompt, showToast } = useAppShell();
  const { getContact, conversations, updateContact } = useHubStore();
  const { tasks, projects } = useProjectsStore();

  const contact = getContact(contactId);
  if (!contact) return null;

  const contactConversations = uniqueById(conversations.filter((c) => c.contactId === contactId));

  // Get tasks assigned to this contact
  const assignedTasks = tasks.filter((t) => t.assignees?.includes(contactId));
  const pendingTasks = assignedTasks.filter((t) => t.status !== 'done');
  const completedTasks = assignedTasks.filter((t) => t.status === 'done');

  // Get projects where this contact is involved
  const involvedProjects = projects.filter((p) =>
    p.team?.some((m) => m.userId === contactId || m.displayName === contact.displayName)
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.75rem',
        padding: '0.75rem 1rem', borderBottom: '1px solid var(--line)',
      }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', padding: '0.25rem', cursor: 'pointer' }}>
          <ArrowLeft size={20} />
        </button>
        <div style={{
          width: '48px', height: '48px', borderRadius: '50%',
          background: 'var(--brand)', color: 'white',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '1.25rem', fontWeight: 600,
        }}>
          {contact.displayName.charAt(0).toUpperCase()}
        </div>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600 }}>{contact.displayName}</h2>
          {contact.role && (
            <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>{contact.role}</span>
          )}
          {contact.company && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              <Building size={12} /> {contact.company}
            </div>
          )}
        </div>
        {onEdit && (
          <button
            onClick={onEdit}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.25rem',
              padding: '0.5rem 0.75rem', background: 'var(--brand)', color: 'white',
              border: 'none', borderRadius: '0.375rem', cursor: 'pointer',
              fontSize: '0.8125rem', fontWeight: 500,
            }}
          >
            <Edit size={14} /> {t('common.edit')}
          </button>
        )}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '1rem' }}>
        {/* Quick Stats */}
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.5rem 0.75rem', background: '#dcfce7', borderRadius: '0.5rem',
          }}>
            <CheckCircle2 size={16} style={{ color: '#22c55e' }} />
            <span style={{ fontSize: '0.8125rem', fontWeight: 500 }}>
              {completedTasks.length}/{assignedTasks.length} Task
            </span>
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.5rem 0.75rem', background: '#dbeafe', borderRadius: '0.5rem',
          }}>
            <MessageCircle size={16} style={{ color: '#3b82f6' }} />
            <span style={{ fontSize: '0.8125rem', fontWeight: 500 }}>
              {contactConversations.length} {t('hub.conversations')}
            </span>
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.5rem 0.75rem', background: '#f3e8ff', borderRadius: '0.5rem',
          }}>
            <FolderKanban size={16} style={{ color: '#8b5cf6' }} />
            <span style={{ fontSize: '0.8125rem', fontWeight: 500 }}>
              {involvedProjects.length} {t('nav.projects')}
            </span>
          </div>
        </div>

        {/* Contact Info */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem' }}>
          {contact.handles.email && (
            <a href={`mailto:${contact.handles.email}`} style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.5rem 0.75rem', background: '#dbeafe', borderRadius: '0.375rem',
              color: '#3b82f6', textDecoration: 'none', fontSize: '0.875rem',
            }}>
              <Mail size={14} /> {contact.handles.email}
            </a>
          )}
          {contact.handles.phone && (
            <a href={`tel:${contact.handles.phone}`} style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.5rem 0.75rem', background: '#dcfce7', borderRadius: '0.375rem',
              color: '#22c55e', textDecoration: 'none', fontSize: '0.875rem',
            }}>
              <Phone size={14} /> {contact.handles.phone}
            </a>
          )}
          {contact.handles.whatsapp && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.5rem 0.75rem', background: '#dcfce7', borderRadius: '0.375rem',
              color: '#22c55e', fontSize: '0.875rem',
            }}>
              <MessageCircle size={14} /> WhatsApp: {contact.handles.whatsapp}
            </div>
          )}
          {contact.handles.telegram && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.5rem 0.75rem', background: '#e0f2fe', borderRadius: '0.375rem',
              color: '#0088cc', fontSize: '0.875rem',
            }}>
              <Send size={14} /> @{contact.handles.telegram}
            </div>
          )}
        </div>

        {/* Tags */}
        {contact.tags.length > 0 && (
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
              {contact.tags.map((tag, i) => (
                <span key={i} style={{
                  display: 'flex', alignItems: 'center', gap: '0.25rem',
                  padding: '0.25rem 0.5rem', background: '#f3f4f6', borderRadius: '9999px',
                  fontSize: '0.75rem',
                }}>
                  <Tag size={10} /> {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Tasks Section */}
        {assignedTasks.length > 0 && (
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem',
            }}>
              <CheckCircle2 size={16} style={{ color: '#22c55e' }} />
              {t('hub.assignedTasks', { count: assignedTasks.length })}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
              {pendingTasks.slice(0, 3).map((task) => (
                <div key={task.id} style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.5rem 0.75rem', background: 'var(--bg)', borderRadius: '0.375rem',
                }}>
                  <Clock size={14} style={{ color: '#f59e0b' }} />
                  <span style={{ flex: 1, fontSize: '0.875rem' }}>{task.title}</span>
                  {task.dueAt && (
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      {new Date(task.dueAt).toLocaleDateString(locale, { day: '2-digit', month: 'short' })}
                    </span>
                  )}
                </div>
              ))}
              {pendingTasks.length > 3 && (
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', padding: '0.25rem 0.75rem' }}>
                  {t('hub.moreTasksInProgress', { count: pendingTasks.length - 3 })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Projects Section */}
        {involvedProjects.length > 0 && (
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem',
            }}>
              <FolderKanban size={16} style={{ color: '#8b5cf6' }} />
              {t('hub.projects', { count: involvedProjects.length })}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
              {involvedProjects.slice(0, 3).map((project) => (
                <div key={project.id} style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.5rem 0.75rem', background: 'var(--bg)', borderRadius: '0.375rem',
                }}>
                  <FolderKanban size={14} style={{ color: '#8b5cf6' }} />
                  <span style={{ flex: 1, fontSize: '0.875rem' }}>{project.title}</span>
                  {project.clientName && (
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      {project.clientName}
                    </span>
                  )}
                </div>
              ))}
              {involvedProjects.length > 3 && (
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', padding: '0.25rem 0.75rem' }}>
                  {t('hub.moreProjects', { count: involvedProjects.length - 3 })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Conversations */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem',
          }}>
            <MessageCircle size={16} style={{ color: '#3b82f6' }} />
            {t('hub.conversations')} ({contactConversations.length})
          </div>
          {contactConversations.length === 0 ? (
            <div style={{
              padding: '1rem', background: 'var(--bg)', borderRadius: '0.5rem',
              textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.875rem',
            }}>
              {t('hub.noConversation')}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
              {contactConversations.slice(0, 5).map((conv) => (
                <div key={conv.id} style={{
                  padding: '0.5rem 0.75rem', background: conv.unread ? 'var(--brand-soft, #ecfdf5)' : 'var(--bg)',
                  borderRadius: '0.375rem', display: 'flex', alignItems: 'center', gap: '0.75rem',
                }}>
                  <ChannelIcon channel={conv.channel} size={14} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: conv.unread ? 600 : 500, fontSize: '0.875rem' }}>{conv.title}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      {formatTime(conv.lastAt, t, locale)}
                    </div>
                  </div>
                  {(conv.unread || 0) > 0 && (
                    <span style={{
                      minWidth: '18px', height: '18px', borderRadius: '50%',
                      background: 'var(--brand)', color: 'white',
                      fontSize: '0.625rem', fontWeight: 600,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {conv.unread}
                    </span>
                  )}
                </div>
              ))}
              {contactConversations.length > 5 && (
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', padding: '0.25rem 0.75rem' }}>
                  {t('hub.moreConversations', { count: contactConversations.length - 5 })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Notes */}
        {contact.notes && (
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem',
            }}>
              <FileText size={16} style={{ color: '#6b7280' }} />
              {t('hub.notes')}
            </div>
            <div style={{
              padding: '0.75rem', background: 'var(--bg)', borderRadius: '0.5rem',
              fontSize: '0.875rem', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap',
            }}>
              {contact.notes}
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            className="btn btn--primary"
            style={{ flex: 1 }}
            onClick={() => showToast(t('hub.featureComingSoon'))}
          >
            <MessageCircle size={16} /> {t('hub.message')}
          </button>
          <button
            className="btn btn--outline"
            style={{ flex: 1 }}
            onClick={() => showToast(t('hub.callFeatureComingSoon'))}
          >
            <Phone size={16} /> {t('hub.callAction')}
          </button>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '0.75rem',
      padding: '0.5rem 0.75rem', background: 'var(--bg)', borderRadius: '0.5rem',
    }}>
      <span style={{ color: 'var(--text-muted)' }}>{icon}</span>
      <div>
        <div style={{ fontSize: '0.625rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>{label}</div>
        <div style={{ fontSize: '0.875rem' }}>{value}</div>
      </div>
    </div>
  );
}

function StatBadge({ label, count, variant }: { label: string; count: number; variant: 'ok' | 'warn' | 'blue' }) {
  const styles = {
    ok: { bg: '#dcfce7', color: '#22c55e' },
    warn: { bg: '#fef3c7', color: '#f59e0b' },
    blue: { bg: '#dbeafe', color: '#3b82f6' },
  };
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '0.375rem',
      padding: '0.25rem 0.625rem', borderRadius: '9999px',
      fontSize: '0.75rem', fontWeight: 500,
      background: styles[variant].bg, color: styles[variant].color,
    }}>
      <span>{count}</span>
      <span style={{ opacity: 0.7 }}>{label}</span>
    </div>
  );
}

// =============================================================================
// MAIN VIEW
// =============================================================================

interface HubViewProps {
  contacts?: ContactItem[];
  onContactClick?: (contact: ContactItem) => void;
}

// Channel labels for breadcrumbs - use translation keys
const CHANNEL_LABEL_KEYS: Record<HubChannel, string> = {
  email: 'hub.channels.email',
  calls: 'hub.channels.calls',
  messages: 'hub.channels.messages',
  whatsapp: 'hub.channels.whatsapp',
  telegram: 'hub.channels.telegram',
  links: 'hub.channels.links',
  contacts: 'hub.contacts',
};

export function HubView({ contacts: propContacts, onContactClick }: HubViewProps) {
  const { t, language } = useTranslation();
  const locale = language === 'it' ? 'it-IT' : 'en-US';

  // URL-driven state for channel/folder navigation
  const {
    channel: urlChannel,
    folder: urlFolder,
    category: urlCategory,
    entityId: urlEntityId,
    isWidgetOpen,
    goToHub,
    navigateToChannel,
    navigateToFolder,
    navigateToCategory,
    openEntity,
    closeEntity,
  } = useHubDeepLink();

  // Global entity modal - used for opening entities from channel groups
  const { openEntity: openGlobalEntity } = useEntityModal();

  const [emailStats, setEmailStats] = useState<{ inbox: number; sent: number }>({ inbox: 0, sent: 0 });

  const {
    activeFilters,
    setActiveFilter,
    showToast,
    setHeaderBreadcrumbs,
    setHeaderTitleOverride,
    setDrawerSectionsOverride,
  } = useAppShell();
  const {
    conversations,
    contacts,
    selectedConversationId,
    selectedContactId,
    selectConversation,
    selectContact,
    setActiveFilterKey,
  } = useHubStore();

  // Create refs for stable callbacks in useEffect
  const goToHubRef = useRef(goToHub);
  const navigateToChannelRef = useRef(navigateToChannel);
  goToHubRef.current = goToHub;
  navigateToChannelRef.current = navigateToChannel;

  // Data loading moved to useAppInit with loadFromServer()

  // Load email stats from IMAP
  useEffect(() => {
    const loadEmailStats = async () => {
      try {
        const folders = await listFolders();
        const inbox = folders.find(f => f.name.toLowerCase() === 'inbox' || f.specialUse === '\\Inbox');
        const sent = folders.find(f =>
          f.specialUse === '\\Sent' ||
          f.name.toLowerCase().includes('sent') ||
          f.name.toLowerCase().includes('inviata')
        );
        setEmailStats({
          inbox: inbox?.messagesUnread || 0,
          sent: sent?.messagesTotal || 0,
        });
      } catch {
        // IMAP not connected, use default values
      }
    };
    loadEmailStats();
  }, []);

  // Sync filters
  useEffect(() => {
    const filter = activeFilters.hub || 'all';
    setActiveFilterKey(filter);
  }, [activeFilters.hub, setActiveFilterKey]);

  // Set breadcrumbs based on URL navigation level
  useEffect(() => {
    const breadcrumbs: { label: string; onClick?: () => void }[] = [];

    // First crumb: "Hub" - goes to hub main
    breadcrumbs.push({
      label: t('nav.hub'),
      onClick: urlChannel ? () => goToHubRef.current() : undefined,
    });

    // Second crumb: Channel name (if in a channel)
    if (urlChannel) {
      breadcrumbs.push({
        label: t(CHANNEL_LABEL_KEYS[urlChannel]) || urlChannel,
        onClick: urlFolder ? () => navigateToChannelRef.current(urlChannel) : undefined,
      });
    }

    // Third crumb: Folder name (if in a folder, only for email)
    if (urlChannel === 'email' && urlFolder) {
      breadcrumbs.push({
        label: urlFolder === 'INBOX' ? t('hub.inbox') : urlFolder,
      });
    }

    setHeaderBreadcrumbs(breadcrumbs);
    setHeaderTitleOverride(null);

    return () => {
      setHeaderBreadcrumbs(null);
    };
  }, [urlChannel, urlFolder, setHeaderBreadcrumbs, setHeaderTitleOverride, t]);

  // Set up sidebar with channels
  // IMPORTANT: When email channel is active, HubEmailChannel manages its own drawer sections
  // So we skip setting drawer here to avoid overriding email folders
  useEffect(() => {
    // Skip drawer setup when in email channel - HubEmailChannel handles its own drawer
    // Use string comparison to avoid TypeScript type narrowing issues
    if (urlChannel && (urlChannel as string) === 'email') {
      return;
    }

    const channelItems = [
      {
        id: 'email',
        label: t('hub.channels.email'),
        hint: t('hub.hints.email'),
        icon: 'mail',
        count: emailStats.inbox,
        active: urlChannel === 'email',
        onClick: () => navigateToChannel('email'),
      },
      {
        id: 'calls',
        label: t('hub.channels.calls'),
        hint: t('hub.hints.calls'),
        icon: 'phone',
        active: urlChannel === 'calls',
        onClick: () => navigateToChannel('calls'),
      },
      {
        id: 'messages',
        label: t('hub.channels.messages'),
        hint: t('hub.hints.sms'),
        icon: 'message-square',
        active: urlChannel === 'messages',
        onClick: () => navigateToChannel('messages'),
      },
      {
        id: 'whatsapp',
        label: t('hub.channels.whatsapp'),
        hint: t('hub.hints.whatsapp'),
        icon: 'message-circle',
        active: urlChannel === 'whatsapp',
        onClick: () => navigateToChannel('whatsapp'),
      },
      {
        id: 'telegram',
        label: t('hub.channels.telegram'),
        hint: t('hub.hints.telegram'),
        icon: 'send',
        active: urlChannel === 'telegram',
        onClick: () => navigateToChannel('telegram'),
      },
      {
        id: 'links',
        label: t('hub.channels.links'),
        hint: t('hub.hints.links'),
        icon: 'link-2',
        active: urlChannel === 'links',
        onClick: () => navigateToChannel('links'),
      },
    ];

    const otherItems = [
      {
        id: 'contacts',
        label: t('hub.contacts'),
        hint: t('hub.hints.contacts'),
        icon: 'users',
        count: contacts.length,
        active: urlChannel === 'contacts',
        onClick: () => navigateToChannel('contacts'),
      },
    ];

    setDrawerSectionsOverride([
      { title: t('hub.channels.all'), items: channelItems },
      { title: t('hub.addressBook'), items: otherItems },
    ]);
    // No cleanup needed - AppShell resets sections on view change
  }, [urlChannel, emailStats, contacts.length, navigateToChannel, setDrawerSectionsOverride, t]);

  // Selected conversation - use centralized widget system (universal widgets)
  const selectedConversation = selectedConversationId
    ? conversations.find(c => c.id === selectedConversationId)
    : null;

  // Navigation handlers that update URL
  const handleSelectChannel = useCallback((channel: ChannelType, subgroup?: string) => {
    if (subgroup && channel === 'email') {
      navigateToFolder(subgroup);
    } else {
      navigateToChannel(channel as HubChannel);
    }
  }, [navigateToChannel, navigateToFolder]);

  const handleBackToChannels = useCallback(() => {
    goToHub();
  }, [goToHub]);

  // Handle avatar click - open contact profile
  const handleAvatarClick = useCallback((name: string, id?: string) => {
    console.log('Avatar clicked:', name, id);
    if (id) {
      selectContact(id);
    }
  }, [selectContact]);

  // Note: Contact detail is now shown via URL navigation, not store state
  // URL: /hub/contacts/cnt_123 opens ContactWidget in panel

  // Render content based on selected channel (from URL)
  const renderChannelContent = () => {
    switch (urlChannel) {
      case 'email':
        return (
          <HubEmailChannel
            onBack={handleBackToChannels}
            initialFolder={urlFolder || undefined}
            initialCategory={urlCategory || undefined}
            selectedEmailId={urlEntityId}
            onSelectEmail={openEntity}
            onCloseEmail={closeEntity}
            onFolderChange={navigateToFolder}
            onCategoryChange={navigateToCategory}
          />
        );
      case 'calls':
        return (
          <CallsChannelView
            onBack={handleBackToChannels}
            selectedCallId={urlEntityId}
            onSelectCall={openEntity}
            onCloseCall={closeEntity}
          />
        );
      case 'whatsapp':
        return (
          <MessagingChannelView
            channel="whatsapp"
            onBack={handleBackToChannels}
            selectedConversationId={urlEntityId}
            onSelectConversation={openEntity}
            onCloseConversation={closeEntity}
          />
        );
      case 'telegram':
        return (
          <MessagingChannelView
            channel="telegram"
            onBack={handleBackToChannels}
            selectedConversationId={urlEntityId}
            onSelectConversation={openEntity}
            onCloseConversation={closeEntity}
          />
        );
      case 'messages':
        return (
          <MessagingChannelView
            channel="sms"
            onBack={handleBackToChannels}
            selectedConversationId={urlEntityId}
            onSelectConversation={openEntity}
            onCloseConversation={closeEntity}
          />
        );
      case 'videocall':
        return (
          <VideocallChannelView
            onBack={handleBackToChannels}
            selectedMeetingId={urlEntityId}
            onSelectMeeting={openEntity}
            onCloseMeeting={closeEntity}
          />
        );
      case 'links':
        return (
          <LinksChannelView onBack={handleBackToChannels} />
        );
      case 'contacts':
        return (
          <ContactsListView
            onSelectContact={(contactId) => openEntity(contactId)}
            selectedContactId={urlEntityId}
            onCloseContact={closeEntity}
          />
        );
      default:
        return (
          <HubChannelGroups
            onSelectChannel={handleSelectChannel}
            emailStats={emailStats}
            onAvatarClick={handleAvatarClick}
            onEntityClick={openGlobalEntity}
          />
        );
    }
  };

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Main content - no custom header needed, breadcrumbs are in AppShell header */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {renderChannelContent()}
        </div>
      </div>

      {/* Conversation Widget - Uses centralized widget system (universal) */}
      {selectedConversation && renderWidgetByType('cnv', {
        entityId: selectedConversation.id,
        onClose: () => selectConversation(null),
        onSave: () => {
          showToast(t('common.saved'));
          selectConversation(null);
        },
      })}
    </>
  );
}

// =============================================================================
// CHANNEL VIEWS FOR NON-EMAIL CHANNELS
// =============================================================================

function CallsChannelView({
  onBack,
  selectedCallId,
  onSelectCall,
  onCloseCall,
}: {
  onBack: () => void;
  selectedCallId?: string;
  onSelectCall?: (id: string) => void;
  onCloseCall?: () => void;
}) {
  const { t, language } = useTranslation();
  const locale = language === 'it' ? 'it-IT' : 'en-US';
  const { conversations } = useHubStore();

  // Filter calls from conversations
  const calls = conversations.filter(c => c.channel === 'call' || c.channel === 'videocall');

  // Get selected call for widget
  const selectedCall = selectedCallId ? calls.find(c => c.id === selectedCallId) : null;

  if (calls.length === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
        <Phone size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
        <h3 style={{ margin: '0 0 0.5rem' }}>{t('hub.channels.calls')}</h3>
        <p>{t('hub.noRecentCalls')}</p>
      </div>
    );
  }

  return (
    <>
      <div className="list">
        {calls.map(call => (
          <div
            key={call.id}
            className="item"
            onClick={() => onSelectCall?.(call.id)}
            style={{ cursor: 'pointer' }}
          >
            <div className="item__icon">
              {call.channel === 'videocall' ? (
                <Video size={18} style={{ color: '#f59e0b' }} />
              ) : (
                <Phone size={18} style={{ color: '#f59e0b' }} />
              )}
            </div>
            <div className="item__main">
              <h3 className="item__title">{call.contactName}</h3>
              <div className="item__meta">
                <span>{call.channel === 'videocall' ? t('hub.channels.videocall') : t('hub.channels.call')}</span>
                {call.lastMessage && (
                  <span style={{ color: 'var(--text-muted)' }}>
                    {new Date(call.lastMessage.at).toLocaleString(locale, {
                      day: '2-digit',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                )}
              </div>
            </div>
            <div className="item__right">
              <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />
            </div>
          </div>
        ))}
      </div>

      {/* Call Widget */}
      {selectedCall && renderWidgetByType('cal', {
        entityId: selectedCallId,
        onClose: onCloseCall,
      })}
    </>
  );
}

function VideocallChannelView({
  onBack,
  selectedMeetingId,
  onSelectMeeting,
  onCloseMeeting,
}: {
  onBack: () => void;
  selectedMeetingId?: string;
  onSelectMeeting?: (id: string) => void;
  onCloseMeeting?: () => void;
}) {
  const { t } = useTranslation();
  const { conversations } = useHubStore();
  const [showNewMeeting, setShowNewMeeting] = useState(false);

  // Filter videocall conversations
  const videocalls = conversations.filter(c => c.channel === 'videocall');

  // Get selected for widget
  const selected = selectedMeetingId ? videocalls.find(c => c.id === selectedMeetingId) : null;

  return (
    <>
      {/* Header with New Meeting button */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '1rem 1.5rem',
        borderBottom: '1px solid var(--line, #e5e7eb)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <Video size={20} style={{ color: '#7C3AED' }} />
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>
            {t('hub.channels.videocall')}
          </h2>
          <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
            ({videocalls.length})
          </span>
        </div>
        <button
          onClick={() => setShowNewMeeting(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 16px',
            border: 'none',
            borderRadius: '8px',
            background: '#7C3AED',
            color: 'white',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <Plus size={16} />
          {t('videocall.newMeeting')}
        </button>
      </div>

      {videocalls.length === 0 ? (
        <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
          <Video size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
          <h3 style={{ margin: '0 0 0.5rem' }}>{t('videocall.noMeetings')}</h3>
          <p>{t('videocall.noMeetingsHint')}</p>
        </div>
      ) : (
        <div className="list">
          {videocalls.map(vc => (
            <div
              key={vc.id}
              className="item"
              onClick={() => onSelectMeeting?.(vc.id)}
              style={{ cursor: 'pointer' }}
            >
              <div className="item__icon">
                <Video size={18} style={{ color: '#7C3AED' }} />
              </div>
              <div className="item__main">
                <h3 className="item__title">{vc.contactName || t('videocall.title')}</h3>
                <div className="item__meta">
                  <span>{t('hub.channels.videocall')}</span>
                </div>
              </div>
              <div className="item__right">
                <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Video Call Widget for selected meeting */}
      {selected && renderWidgetByType('vcl', {
        entityId: selectedMeetingId,
        onClose: onCloseMeeting,
      })}

      {/* New Meeting Widget */}
      {showNewMeeting && renderWidgetByType('vcl', {
        onClose: () => setShowNewMeeting(false),
        onSave: () => setShowNewMeeting(false),
      })}
    </>
  );
}

function MessagingChannelView({
  channel,
  onBack,
  selectedConversationId,
  onSelectConversation,
  onCloseConversation,
}: {
  channel: 'whatsapp' | 'telegram' | 'sms';
  onBack: () => void;
  selectedConversationId?: string;
  onSelectConversation?: (id: string) => void;
  onCloseConversation?: () => void;
}) {
  const { t, language } = useTranslation();
  const locale = language === 'it' ? 'it-IT' : 'en-US';
  const { conversations } = useHubStore();

  const Icon = channel === 'whatsapp' ? MessageCircle : channel === 'sms' ? MessageCircle : Send;
  const labelKey = channel === 'whatsapp' ? 'hub.channels.whatsapp' : channel === 'sms' ? 'hub.channels.messages' : 'hub.channels.telegram';
  const label = t(labelKey);
  const color = channel === 'whatsapp' ? '#22c55e' : channel === 'sms' ? '#6b7280' : '#0088cc';

  // Filter conversations by channel
  const channelConversations = conversations.filter(c => c.channel === channel);

  // Get selected conversation for widget
  const selectedConv = selectedConversationId
    ? channelConversations.find(c => c.id === selectedConversationId)
    : null;

  if (channelConversations.length === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
        <Icon size={48} style={{ marginBottom: '1rem', opacity: 0.5, color }} />
        <h3 style={{ margin: '0 0 0.5rem' }}>{label}</h3>
        <p>{t('hub.noConversationChannel', { channel: label })}</p>
      </div>
    );
  }

  return (
    <>
      <div className="list">
        {channelConversations.map(conv => {
          const hasUnread = (conv.unread || 0) > 0;

          return (
            <div
              key={conv.id}
              className="item"
              onClick={() => onSelectConversation?.(conv.id)}
              style={{
                cursor: 'pointer',
                background: hasUnread ? `${color}10` : undefined,
              }}
            >
              <div className="item__icon">
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: color,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}>
                  {conv.contactName.charAt(0).toUpperCase()}
                </div>
              </div>
              <div className="item__main">
                <h3 className="item__title" style={{ fontWeight: hasUnread ? 600 : 500 }}>
                  {conv.contactName}
                </h3>
                {conv.lastMessage && (
                  <p style={{
                    margin: '0.125rem 0 0',
                    fontSize: '0.8125rem',
                    color: 'var(--text-muted)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {conv.lastMessage.content}
                  </p>
                )}
                <div className="item__meta">
                  {hasUnread && (
                    <span
                      style={{
                        background: color,
                        color: 'white',
                        padding: '0.125rem 0.375rem',
                        borderRadius: '0.625rem',
                        fontSize: '0.625rem',
                        fontWeight: 600,
                      }}
                    >
                      {t('hub.newCount', { count: conv.unread })}
                    </span>
                  )}
                  {conv.lastMessage && (
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                      {formatRelativeTime(conv.lastMessage.at, t, locale)}
                    </span>
                  )}
                </div>
              </div>
              <div className="item__right">
                <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Conversation Widget */}
      {selectedConv && renderWidgetByType('cnv', {
        entityId: selectedConversationId,
        onClose: onCloseConversation,
      })}
    </>
  );
}

// Helper for relative time formatting
function formatRelativeTime(isoDate: string, t: (key: string, params?: Record<string, unknown>) => string, locale: string): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return t('hub.now');
  if (diffMins < 60) return t('hub.minutesAgo', { count: diffMins });
  if (diffHours < 24) return t('hub.hoursAgo', { count: diffHours });
  if (diffDays === 1) return t('common.yesterday');
  if (diffDays < 7) return t('common.daysAgo', { count: diffDays });
  return date.toLocaleDateString(locale, { day: '2-digit', month: 'short' });
}

